import { useParams, Link } from "react-router-dom";
import { srcSetFor } from "@/lib/responsiveImage";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

import { FadeUp } from "@/components/Motion";
import SeoHead from "@/components/SeoHead";
import RichContent, { SectionLoading, SectionError } from "@/components/RichContent";
import NotFound from "@/pages/NotFound";
import { useBlog } from "@/hooks/usePublicData";
import { formatSGDate } from "@/lib/format";

export default function BlogDetail() {
  const { slug } = useParams();
  const { data, isLoading, isError, error, refetch } = useBlog(slug);

  if (isLoading) return <SectionLoading label="Loading article" />;

  // A 404 is a real answer about this URL, not a failure to reach the server —
  // render the not-found page rather than a "try again" panel.
  if (isError && error && error.status === 404) return <NotFound />;
  if (isError) return <SectionError onRetry={refetch} />;

  const b = data.item;
  const related = data.related || [];

  return (
    <>
      {/* The server already injected the authoritative tags; this keeps the tab
          title correct after a client-side navigation. */}
      <SeoHead title={b.seo.title} description={b.seo.description} />

      <section className="bg-[#060f1e] pt-8 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 text-sm text-[#9ca3af] hover:text-[#cca24d] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> All insights
          </Link>

          <div className="mt-10">
            <div className="flex items-center flex-wrap gap-3 mb-6">
              {b.category && <span className="overline">{b.category}</span>}
              {b.publishedAt && (
                <>
                  <span className="text-xs text-[#9ca3af]">·</span>
                  <span className="text-xs text-[#9ca3af]">{formatSGDate(b.publishedAt)}</span>
                </>
              )}
              {b.readingTimeMinutes && (
                <>
                  <span className="text-xs text-[#9ca3af]">·</span>
                  <span className="text-xs text-[#9ca3af]">{b.readingTimeMinutes} min read</span>
                </>
              )}
            </div>

            <FadeUp>
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-[#f7f5f0] font-light tracking-tight leading-[0.98]">
                {b.title}
              </h1>
            </FadeUp>

            {b.excerpt && (
              <FadeUp delay={0.1}>
                <p className="mt-8 text-[#d8d8d8] text-lg leading-relaxed">{b.excerpt}</p>
              </FadeUp>
            )}

            <FadeUp delay={0.15}>
              <div className="mt-8 text-xs text-[#9ca3af] tracking-widest uppercase">
                By {b.author || "PIRAC Research and Advisory Team"}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {b.featuredImage && (
        <section className="bg-[#060f1e]">
          <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
            <div className="relative aspect-[16/9] overflow-hidden">
              <img
                src={b.featuredImage}
                srcSet={srcSetFor(b.featuredImage)}
                sizes="(min-width: 1024px) 70vw, 92vw"
                alt={b.featuredImageAlt || b.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#060f1e] py-20 lg:py-28">
        <div className="mx-auto max-w-[820px] px-6 lg:px-10">
          <RichContent html={b.content} />

          {b.tags && b.tags.length > 0 && (
            <div className="mt-12 flex flex-wrap gap-2">
              {b.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 border border-[#1a2940] text-xs text-[#9ca3af] tracking-wide"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-[#1a2940] text-xs text-[#9ca3af] uppercase tracking-widest">
            Editorial by {b.author || "PIRAC Research and Advisory Team"}
            {b.publishedAt && ` · ${formatSGDate(b.publishedAt)}`}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-[#0c1728] border-t border-[#1a2940] py-24">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <div className="overline mb-8">Continue reading</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((o) => (
                <Link key={o.id} to={o.path} className="group block">
                  {o.category && (
                    <div className="flex items-center gap-3 text-xs text-[#cca24d]/80 uppercase tracking-widest mb-3">
                      {o.category}
                    </div>
                  )}
                  <h3 className="font-serif text-2xl md:text-3xl text-[#f7f5f0] leading-tight group-hover:text-[#cca24d] transition-colors">
                    {o.title}
                  </h3>
                  <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#d8d8d8] group-hover:text-[#cca24d] transition-colors">
                    Read <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
